import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getSocket, socketSupported } from '../socket';
import { createPeerConnection, getMicrophoneStream, stopStream } from '../webrtc';

const CallContext = createContext(null);

const ERROR_DISPLAY_MS = 3000;

// idle | ringing-outgoing | ringing-incoming | active | error
export function CallProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('idle');
  const [peer, setPeer] = useState(null); // { id, name }
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState('');

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const incomingOfferRef = useRef(null);
  const errorTimerRef = useRef(null);

  // Releases the mic/peer connection without touching visible state --
  // shared by the silent (teardown) and the message-first (failWithError)
  // paths below.
  const cleanupResources = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    stopStream(localStreamRef.current);
    localStreamRef.current = null;
    pendingCandidatesRef.current = [];
    incomingOfferRef.current = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  }, []);

  // Normal end of a call (local hangup, remote ended) -- nothing to
  // explain, so it's fine to drop straight back to idle.
  const teardown = useCallback(() => {
    if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
    cleanupResources();
    setStatus('idle');
    setPeer(null);
    setMuted(false);
    setError('');
  }, [cleanupResources]);

  // A call that couldn't happen (declined/busy/unavailable/setup failure):
  // clean up resources immediately, but keep the overlay up long enough
  // to actually read why, instead of resetting straight to idle (which
  // used to make CallOverlay unmount in the same render the message was
  // set, so it was never visibly shown).
  const failWithError = useCallback((message) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    cleanupResources();
    setError(message);
    setStatus('error');
    setMuted(false);
    errorTimerRef.current = setTimeout(() => {
      setStatus('idle');
      setPeer(null);
      setError('');
      errorTimerRef.current = null;
    }, ERROR_DISPLAY_MS);
  }, [cleanupResources]);

  const dismissError = useCallback(() => {
    if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
    setStatus('idle');
    setPeer(null);
    setError('');
  }, []);

  const setupPeerConnection = useCallback((toUserId) => {
    const pc = createPeerConnection();
    return pc.then((connection) => {
      pcRef.current = connection;
      connection.onicecandidate = (e) => {
        if (e.candidate) getSocket()?.emit('call:ice-candidate', { toUserId, candidate: e.candidate });
      };
      connection.ontrack = (e) => {
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = e.streams[0];
      };
      connection.onconnectionstatechange = () => {
        if (['failed', 'closed'].includes(connection.connectionState)) teardown();
      };
      return connection;
    });
  }, [teardown]);

  const startCall = useCallback(async (toUserId, toName) => {
    if (!socketSupported()) return;
    setError('');
    setPeer({ id: toUserId, name: toName });
    try {
      const pc = await setupPeerConnection(toUserId);
      const stream = await getMicrophoneStream();
      localStreamRef.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      setStatus('ringing-outgoing');
      getSocket()?.emit('call:invite', { toUserId, offer, fromName: user?.name });
    } catch (err) {
      failWithError(err.message || 'Could not start the call.');
    }
  }, [setupPeerConnection, failWithError, user]);

  const acceptCall = useCallback(async () => {
    const incoming = incomingOfferRef.current;
    if (!incoming) return;
    setError('');
    try {
      const pc = await setupPeerConnection(incoming.fromUserId);
      await pc.setRemoteDescription(incoming.offer);

      const stream = await getMicrophoneStream();
      localStreamRef.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      for (const candidate of pendingCandidatesRef.current) await pc.addIceCandidate(candidate);
      pendingCandidatesRef.current = [];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      getSocket()?.emit('call:answer', { toUserId: incoming.fromUserId, answer });
      setStatus('active');
    } catch (err) {
      getSocket()?.emit('call:decline', { toUserId: incoming.fromUserId });
      failWithError(err.message || 'Could not answer the call.');
    }
  }, [setupPeerConnection, failWithError]);

  const declineCall = useCallback(() => {
    if (peer) getSocket()?.emit('call:decline', { toUserId: peer.id });
    teardown();
  }, [peer, teardown]);

  const endCall = useCallback(() => {
    if (peer) getSocket()?.emit('call:end', { toUserId: peer.id });
    teardown();
  }, [peer, teardown]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !muted;
    stream.getAudioTracks().forEach((track) => { track.enabled = !nextMuted; });
    setMuted(nextMuted);
  }, [muted]);

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();
    if (!socket) return;

    const onIncoming = ({ fromUserId, fromName, offer }) => {
      // Already on a call (e.g. another tab accepted) -- let the caller
      // get a busy signal from the server on their next invite instead.
      if (pcRef.current) return;
      incomingOfferRef.current = { fromUserId, offer };
      setPeer({ id: fromUserId, name: fromName });
      setStatus('ringing-incoming');
    };
    const onAnswered = async ({ answer }) => {
      if (!pcRef.current) return;
      await pcRef.current.setRemoteDescription(answer);
      for (const candidate of pendingCandidatesRef.current) await pcRef.current.addIceCandidate(candidate);
      pendingCandidatesRef.current = [];
      setStatus('active');
    };
    const onIceCandidate = async ({ candidate }) => {
      if (pcRef.current?.remoteDescription) {
        await pcRef.current.addIceCandidate(candidate);
      } else {
        pendingCandidatesRef.current.push(candidate);
      }
    };
    const onDeclined = () => failWithError('Call declined.');
    const onEnded = () => teardown();
    const onBusy = () => failWithError('They are already on another call.');
    const onUnavailable = () => failWithError('That employee is not available right now.');

    socket.on('call:incoming', onIncoming);
    socket.on('call:answered', onAnswered);
    socket.on('call:ice-candidate', onIceCandidate);
    socket.on('call:declined', onDeclined);
    socket.on('call:ended', onEnded);
    socket.on('call:busy', onBusy);
    socket.on('call:unavailable', onUnavailable);
    return () => {
      socket.off('call:incoming', onIncoming);
      socket.off('call:answered', onAnswered);
      socket.off('call:ice-candidate', onIceCandidate);
      socket.off('call:declined', onDeclined);
      socket.off('call:ended', onEnded);
      socket.off('call:busy', onBusy);
      socket.off('call:unavailable', onUnavailable);
    };
  }, [user, teardown, failWithError]);

  return (
    <CallContext.Provider value={{ status, peer, muted, error, startCall, acceptCall, declineCall, endCall, toggleMute, dismissError, remoteAudioRef }}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  return useContext(CallContext);
}
